import * as React from "react"
import { isEqual } from "../utils/isEqual"
import { RenderEnvironment, RenderTarget } from "../types/RenderEnvironment"
import { ReactNode } from "react"
import { resetSetStyle } from "../utils/useWebkitFixes"
import { elementForComponent } from "../utils/elementForComponent"
import { MotionStyle } from "framer-motion"

/** @public */
export interface IdentityProps {
    id?: string
}

export interface WillChangeTransformProp {
    willChangeTransform?: boolean
}

/** @public */
export interface LayerProps extends IdentityProps, WillChangeTransformProp {
    children?: ReactNode
    key?: React.Key
    /** @internal */
    _forwardedOverrides?: { [key: string]: any }
}
/**
 * @public
 */
export class Layer<P extends Partial<LayerProps>, S> extends React.Component<P, S> {
    static readonly defaultProps: LayerProps = {}

    static applyWillChange<P extends WillChangeTransformProp>(layer: { props: P }, style: MotionStyle) {
        // The RenderTarget may change at runtime, so willChangeTransform
        // cannot be specified statically. Instead, we set it with a fallback at runtime.
        const willChangeTransform = layer.props.willChangeTransform || RenderTarget.current() !== RenderTarget.canvas
        if (willChangeTransform) {
            style.willChange = "transform"
        }
    }

    /** @internal */
    shouldComponentUpdate(nextProps: P, nextState: S) {
        return this.state !== nextState || !isEqual(this.props, nextProps, false)
    }

    props: Readonly<P> & Readonly<{ children?: ReactNode }>

    private previousZoom = RenderEnvironment.zoom

    /** @internal */
    componentDidUpdate(prevProps: P) {
        const { zoom } = RenderEnvironment

        // Workarounds for WebKit bugs

        // Some styles have to be toggled to take effect in certain situations.
        // Not using type safety, uses lots of internal knowledge for efficiency
        // To use this as a hook, see useWebKitFixes

        const element = elementForComponent(this)
        if (zoom !== this.previousZoom && this.props["blendingMode"] && this.props["blendingMode"] !== "normal") {
            resetSetStyle(element, "mixBlendMode", this.props["blendingMode"])
        }

        if (this.props["clip"] && this.props["radius"] === 0 && prevProps["radius"] !== 0) {
            resetSetStyle(element, "overflow", "hidden", false)
        }
        this.previousZoom = zoom
    }
}
